import React, { useState } from "react";
import {
  HiX,
  HiExternalLink,
  HiShieldCheck,
  HiCog,
  HiViewGrid,
} from "react-icons/hi";
import BankPortalImage from "../assets/project-screenshots/BankPortfolioLMIS.webp";
import ImageViewer from "./ImageViewer";

const FeaturedProjects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [imageViewer, setImageViewer] = useState({
    isOpen: false,
    src: "",
    alt: "",
  });

  const projects = [
    {
      id: 1,
      title: "Commission Enforcement Bank Portal",
      subtitle: "High-stakes financial infrastructure",
      description:
        "Built from the ground up with a focus on deterministic state handling and multi-layer submission locks.",
      keyResult:
        "Zero critical issues in production since launch. QA-verified stability.",
      image: BankPortalImage,
      icon: <HiShieldCheck className="text-3xl text-cyan-500" />,
      technologies: [
        "React 19",
        "TypeScript 5.8",
        "Apollo GraphQL",
        "Tailwind CSS 4.0",
        "Vite 6",
      ],
      highlights: [
        "308.83 kB ultralight payload for entire initial flow",
        "4-level defensive submission pipeline preventing duplicates",
        "Proactive JWT refresh with exponential backoff",
        "Custom PDF generation with responsive layouts",
        "Zero UI libraries - all components custom-built",
      ],
      fullDescription: `A production-grade financial reporting portal designed for regulated environments, built with a strong emphasis on performance, correctness, and defensive system design.

**Architecture Highlights:**
- State-driven logic where UI behavior adapts deterministically based on system period states
- Role-based boundaries with administrative and standard user datasets
- Specialized React Context providers for centralized state management

**Performance Engineering:**
- Lightning-fast 308.83 kB transfer size for complete initial flow
- Two-step search hydration: Light-to-Heavy data loading
- Route-level code splitting with React Suspense
- Memoized logic replacing complex computation lag

**Security & Reliability:**
The "4-Level Lock" system prevents duplicate financial entries:
1. UI Level: Immediate button disabling upon mutation
2. Input Level: Real-time reference number duplication checks
3. Process Level: Period-specific confirmation modals
4. Backend Level: Strict DB constraints and mutation locks

**Quality Assurance:**
Received highest rating from QA team. After three full cycles of testing, only one moderate bug was reported. Zero production escapes since launch.`,
    },
    {
      id: 2,
      title: "EWP-v1 Legacy Stabilization",
      subtitle: "Archaeological engineering and crisis management",
      description:
        "National-level platform for managing work permits and labour data in Ethiopia. Took ownership after senior devs departed - a legacy system requiring full-scale archaeological dig.",
      keyResult:
        "70% reduction in support tickets (daily to weekly). Director-acknowledged top-3 government platform performance.",
      image: null,
      icon: <HiCog className="text-3xl text-cyan-500" />,
      technologies: [
        "React",
        "NestJS",
        "Hasura GraphQL",
        "PostgreSQL",
        "TypeScript",
        "Legacy Codebase",
      ],
      highlights: [
        "Developed frontend and backend modules for work-permit workflows",
        "Integrated GraphQL & REST APIs for performance and scalability",
        "Designed reusable components optimized for data-heavy operations",
        "Zero documentation - thousand-line files with no separation of concerns",
        "Built document hashing middleware to prevent duplicate uploads",
      ],
      fullDescription: `National-level platform for managing work permits and labour data in Ethiopia. I took over major parts of EWP-v1 after senior developers departed - this was more than a legacy system, it was a total mess requiring full-scale archaeological dig.

**The Challenge:**
- Zero documentation and almost no meaningful comments
- Single files running into thousands of lines  
- Rigid component design with no separation of concerns
- Fragile system where every change felt like walking through a minefield

**Technical Solutions:**
- Developed frontend and backend modules for complex work-permit workflows
- Integrated GraphQL & REST APIs for performance and scalability
- Designed reusable components optimized for data-heavy operations
- Built document hashing middleware to prevent duplicate uploads
- Resolved long-standing concurrency issues under heavy production load

**The Impact:**
Support tickets for recurring issues dropped from nearly every day to just a couple every few weeks. During a stakeholder meeting, the director highlighted EWP-v1 as one of the top 3 performing government platforms.

**Engineering Philosophy:**
Worked in parallel on clean EWP-v2 architecture while maintaining system stability. This project embodied leaving systems better than you found them - transforming a system on the brink of failure into a stable, high-performing national platform.`,
    },
  ];

  const openImageViewer = (src, alt) => {
    setImageViewer({ isOpen: true, src, alt });
  };

  const closeImageViewer = () => {
    setImageViewer({ isOpen: false, src: "", alt: "" });
  };

  const openModal = (project) => {
    setSelectedProject(project);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setSelectedProject(null);
    document.body.style.overflow = "unset";
  };

  return (
    <div name="featured" className="w-full bg-[#0a192f] text-gray-300 py-20">
      <div className="max-w-[1200px] mx-auto px-8">
        <div className="pb-12">
          <h2 className="text-4xl sm:text-5xl font-bold inline-block border-b-4 border-cyan-600">
            Featured Projects
          </h2>
          <p className="text-[#8892b0] text-lg mt-4">
            Deep-dive into high-impact engineering solutions
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {projects.map((project) => (
            <div
              key={project.id}
              className="bg-[#112240] rounded-xl border border-cyan-600 border-opacity-20 hover:border-opacity-50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-600/20 overflow-hidden group cursor-pointer"
              onClick={() => openModal(project)}
            >
              {project.image && (
                <div className="w-full h-64 overflow-hidden relative group">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openImageViewer(project.image, project.title);
                    }}
                    className="absolute top-2 right-2 p-2 bg-black bg-opacity-50 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-opacity-70"
                  >
                    <HiViewGrid className="text-lg" />
                  </button>
                </div>
              )}

              <div className="p-8">
                <div className="flex items-center gap-4 mb-4">
                  {project.icon}
                  <div>
                    <h3 className="text-2xl font-bold text-white group-hover:text-cyan-400 transition-colors duration-300">
                      {project.title}
                    </h3>
                    <p className="text-cyan-500 font-medium">
                      {project.subtitle}
                    </p>
                  </div>
                </div>

                <p className="text-[#8892b0] text-lg leading-relaxed mb-4">
                  {project.description}
                </p>

                <div className="bg-cyan-600 bg-opacity-10 border border-cyan-600 border-opacity-30 rounded-lg p-4 mb-6">
                  <p className="text-cyan-400 font-semibold text-sm mb-1">
                    Key Result:
                  </p>
                  <p className="text-white">{project.keyResult}</p>
                </div>

                <div className="space-y-2 mb-6">
                  {project.highlights.slice(0, 3).map((highlight, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <span className="text-cyan-500 mt-1">▹</span>
                      <p className="text-[#8892b0] text-sm">{highlight}</p>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {project.technologies.slice(0, 4).map((tech, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-cyan-600 bg-opacity-10 text-cyan-400 rounded-md text-sm border border-cyan-600 border-opacity-20"
                    >
                      {tech}
                    </span>
                  ))}
                  {project.technologies.length > 4 && (
                    <span className="px-3 py-1 bg-[#8892b0] bg-opacity-10 text-[#8892b0] rounded-md text-sm">
                      +{project.technologies.length - 4} more
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <button className="text-cyan-400 hover:text-cyan-300 font-semibold flex items-center gap-2 group-hover:gap-3 transition-all duration-300">
                    Read Full Details
                    <HiExternalLink className="text-lg" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Modal */}
        {selectedProject && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#112240] rounded-xl border border-cyan-600 border-opacity-30 max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-[#112240] border-b border-cyan-600 border-opacity-20 p-6 flex justify-between items-center">
                <div className="flex items-center gap-4">
                  {selectedProject.icon}
                  <div>
                    <h3 className="text-2xl font-bold text-white">
                      {selectedProject.title}
                    </h3>
                    <p className="text-cyan-500">{selectedProject.subtitle}</p>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="text-[#8892b0] hover:text-white transition-colors duration-300"
                >
                  <HiX className="text-3xl" />
                </button>
              </div>

              <div className="p-6">
                {selectedProject.image && (
                  <div className="mb-8 relative group">
                    <img
                      src={selectedProject.image}
                      alt={selectedProject.title}
                      className="w-full rounded-lg border border-cyan-600 border-opacity-30 hover:border-opacity-50 transition-all duration-300"
                    />
                    <button
                      onClick={() =>
                        openImageViewer(
                          selectedProject.image,
                          selectedProject.title
                        )
                      }
                      className="absolute top-2 right-2 p-2 bg-black bg-opacity-50 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-opacity-70"
                    >
                      <HiViewGrid className="text-lg" />
                    </button>
                  </div>
                )}

                <div className="bg-cyan-600 bg-opacity-10 border border-cyan-600 border-opacity-30 rounded-lg p-4 mb-6">
                  <p className="text-cyan-400 font-semibold mb-2">
                    Key Result:
                  </p>
                  <p className="text-white text-lg">
                    {selectedProject.keyResult}
                  </p>
                </div>

                <div className="prose prose-invert max-w-none">
                  {selectedProject.fullDescription
                    .split("\n\n")
                    .map((paragraph, idx) => {
                      if (
                        paragraph.startsWith("**") &&
                        paragraph.endsWith("**")
                      ) {
                        return (
                          <h4
                            key={idx}
                            className="text-xl font-bold text-cyan-400 mt-6 mb-3"
                          >
                            {paragraph.replace(/\*\*/g, "")}
                          </h4>
                        );
                      }
                      return (
                        <p
                          key={idx}
                          className="text-[#8892b0] leading-relaxed mb-4"
                        >
                          {paragraph}
                        </p>
                      );
                    })}
                </div>

                <div className="mt-8">
                  <h4 className="text-xl font-bold text-white mb-4">
                    Technologies Used
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.technologies.map((tech, idx) => (
                      <span
                        key={idx}
                        className="px-4 py-2 bg-cyan-600 bg-opacity-10 text-cyan-400 rounded-lg text-sm border border-cyan-600 border-opacity-20"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-8">
                  <h4 className="text-xl font-bold text-white mb-4">
                    Key Highlights
                  </h4>
                  <div className="space-y-2">
                    {selectedProject.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <span className="text-cyan-500 mt-1">▹</span>
                        <p className="text-[#8892b0]">{highlight}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Image Viewer */}
        <ImageViewer
          src={imageViewer.src}
          alt={imageViewer.alt}
          isOpen={imageViewer.isOpen}
          onClose={closeImageViewer}
        />
      </div>
    </div>
  );
};

export default FeaturedProjects;
